import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../../components/session/postSessionCard';
import { ContentCard } from '../../../../components/common/ContentCard';
import { ChevronDown, ChevronUp, FileText, Star } from 'react-feather';
import { liveSessionCoachQuestionSet } from '../../../../config/coachFeedbackQuestionSets';
import {
  composeCarryoverText,
  composeSessionInsightsText,
} from './recapEmailBody';
import { ConcernFlagsSection } from '../ConcernFlagsSection';
import type { FeedbackResponseMap } from '../../../../hooks/useCoachPostSession';
import type {
  ComposeEnvelope,
  RecapDraftOutput,
} from '../../../../services/api/aiTypes';
import type { CreateConcernFlagInput } from '../../../../services/api/coachPortal';

/**
 * MED-794 AC6 — Medley Feedback Form card. MED-893 AC7 layered on top.
 * MED-988 (2.6e) converged this onto Medley's real Session Highlights content.
 *
 * The `live_post_session` question set (now the seven `medley` questions plus
 * the SGOS-only `carryover_for_next_session`) lives in
 * `coachFeedbackQuestionSets.ts`. Save Draft and Submit Feedback both go
 * through `coachPortalApi.submitSessionFeedback / updateSessionFeedback`.
 *
 * AC7 wire-up: when AI is on and the recap-draft envelope has a `result`, two
 * fields are pre-populated from the same payload that seeds the Recap Email:
 *   • `session_insights` ← composed from `recap_email.key_themes[]`
 *     (re-targeted from the former `session_themes` field in MED-988)
 *   • `carryover_for_next_session` ← joined from `recap_email.carryover_for_next_session[]`
 * Both are editable. A "Pre-populated from context" pill appears in the form
 * header while any AI-fillable field still matches its AI seed; per-field
 * tinted cards with a "Pre-populated" sparkle badge appear on those fields.
 * The pill and badges disappear the moment the user edits.
 */

const AI_FILLABLE_KEYS = new Set([
  'session_insights',
  'carryover_for_next_session',
]);

// MED-1054 — Figma palette for the Coach Feedback Form card.
const TITLE_COLOR = '#1A1A2E';
const LABEL_COLOR = '#374151';
const HELPER_COLOR = '#9CA3AF';
const SECTION_DIVIDER = '#ECEDEF';
const FIELD_BORDER = '#E5E7EB';
const FIELD_TEXT = '#374151';
// AI "Pre-populated" treatment (purple-tinted card + sparkle badge).
const AI_CARD_BG = '#F6F4FE';
const AI_ACCENT = '#7C4DFF';
const AI_LABEL = '#5A3EB6';
const AI_BADGE_BG = '#F3F0FF';

// Full-bleed section divider: the card pads with `p="lg"`, so the rule is
// pulled back out to the card edges to match the Figma header/footer dividers.
const BLEED_DIVIDER_STYLE = {
  borderTop: `1px solid ${SECTION_DIVIDER}`,
  marginInline: 'calc(-1 * var(--mantine-spacing-lg))',
} as const;

// MED-1035 AC3 — helper line under the Participant Concerns header. Per the
// ticket's Open Questions, the notes-privacy clause is intentionally NOT added
// yet (gated on the companion AI-normalizer story).
const CONCERN_HELPER_TEXT =
  'The concern type you select may help inform AI prep for future sessions.';

interface Props {
  responses: FeedbackResponseMap;
  onChange: (key: string, value: string) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  saving: boolean;
  submittedAt: string | null;
  recapDraftEnvelope: ComposeEnvelope<RecapDraftOutput> | null;
  // MED-1035 — Participant Concerns picker (Part B). Coach-entered, never AI
  // seeded, bundled into the feedback save/submit payload by the hook.
  participants: Array<{
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  concernFlags: CreateConcernFlagInput[];
  onConcernFlagsChange: (flags: CreateConcernFlagInput[]) => void;
  // AC8 — Medley coaches only; partner coaches don't see the section.
  canFlagConcerns: boolean;
}

export function MedleyFeedbackFormCard({
  responses,
  onChange,
  onSaveDraft,
  onSubmit,
  saving,
  submittedAt,
  recapDraftEnvelope,
  participants,
  concernFlags,
  onConcernFlagsChange,
  canFlagConcerns,
}: Props) {
  const isSubmitted = submittedAt !== null;
  // MED-1077 — collapse to a "Submitted" summary on the submit action; an
  // expand chevron lets the coach reopen to review/update (the form stays
  // editable post-submit, unlike the peer card). Loads expanded when unsubmitted.
  const [expanded, setExpanded] = useState<boolean>(!isSubmitted);
  useEffect(() => {
    if (isSubmitted) setExpanded(false);
  }, [isSubmitted]);

  const aiInitial = useMemo<Record<string, string> | null>(() => {
    if (!recapDraftEnvelope?.result) return null;
    const recap = recapDraftEnvelope.result.recap_email;
    return {
      session_insights: composeSessionInsightsText(recap.key_themes),
      carryover_for_next_session: composeCarryoverText(
        recap.carryover_for_next_session
      ),
    };
  }, [recapDraftEnvelope]);

  // One-shot seed: only fill empty AI-fillable fields. Existing responses
  // (e.g., a coach who already submitted feedback) always win.
  useEffect(() => {
    if (!aiInitial) return;
    for (const key of AI_FILLABLE_KEYS) {
      if (!responses[key] && aiInitial[key]) {
        onChange(key, aiInitial[key]);
      }
    }
    // Deliberately keyed only on aiInitial — the seed is one-shot per envelope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiInitial]);

  const isAiPrefilled = (key: string): boolean => {
    if (!aiInitial || !AI_FILLABLE_KEYS.has(key)) return false;
    const seed = aiInitial[key] ?? '';
    return seed.length > 0 && (responses[key] ?? '') === seed;
  };

  const anyAiPrefilled =
    isAiPrefilled('session_insights') ||
    isAiPrefilled('carryover_for_next_session');

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap={8} align="center" wrap="nowrap">
            <FileText size={18} color={TITLE_COLOR} aria-hidden="true" />
            <Text fz={15} fw={700} c={TITLE_COLOR}>
              Medley Feedback Form
            </Text>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {anyAiPrefilled && (
              <Badge
                size="sm"
                variant="light"
                styles={{
                  root: {
                    backgroundColor: AI_BADGE_BG,
                    color: AI_LABEL,
                    textTransform: 'none',
                    fontWeight: 500,
                  },
                }}
              >
                Pre-populated from context
              </Badge>
            )}
            {isSubmitted && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setExpanded(e => !e)}
                aria-label={
                  expanded ? 'Collapse feedback form' : 'Expand feedback form'
                }
              >
                {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </ActionIcon>
            )}
          </Group>
        </Group>
      }
    >
      {isSubmitted && !expanded && (
        <Text fz={13} c={HELPER_COLOR}>
          Feedback submitted. Expand to review or update your responses.
        </Text>
      )}

      <Collapse in={expanded}>
        <Stack gap="md">
          <Box style={BLEED_DIVIDER_STYLE} />

          <Stack gap="lg">
            {liveSessionCoachQuestionSet.questions.map(q => {
              const aiFilled = isAiPrefilled(q.key);
              // Inline gray subtext to the right of the label (AC2 / Figma). The
              // AI insights field shows "Pre-filled from your notes"; every other
              // field shows its own helper copy (AC3 — copy unchanged).
              const subtext =
                aiFilled && q.key === 'session_insights'
                  ? 'Pre-filled from your notes'
                  : q.helperText;
              return (
                <Stack key={q.key} gap={8}>
                  <Group gap={8} align="baseline" wrap="wrap">
                    <Text fz={13} fw={600} c={LABEL_COLOR}>
                      {q.questionText}
                    </Text>
                    {subtext && (
                      <Text fz={12} c={HELPER_COLOR}>
                        {subtext}
                      </Text>
                    )}
                  </Group>
                  {aiFilled ? (
                    // AC4 — purple-tinted "Pre-populated" card with a sparkle
                    // badge; the textarea blends into the tint so the seeded text
                    // reads as content, not an input. Only while AI-seeded.
                    <Box
                      style={{
                        backgroundColor: AI_CARD_BG,
                        borderRadius: 8,
                        padding: '12px 14px',
                      }}
                    >
                      <Stack gap={8}>
                        <Group gap={5} align="center">
                          <Star size={12} color={AI_ACCENT} fill={AI_ACCENT} />
                          <Text
                            fz={11}
                            fw={600}
                            c={AI_LABEL}
                            style={{ letterSpacing: 0.2 }}
                          >
                            Pre-populated
                          </Text>
                        </Group>
                        <Textarea
                          value={responses[q.key] ?? ''}
                          onChange={e => onChange(q.key, e.currentTarget.value)}
                          placeholder={q.placeholder}
                          autosize
                          minRows={2}
                          maxRows={8}
                          variant="unstyled"
                          styles={{
                            input: {
                              color: FIELD_TEXT,
                              fontSize: 13,
                              lineHeight: 1.55,
                              padding: 0,
                              // NOTE: do NOT set minHeight/maxHeight/height here.
                              // `autosize` (react-textarea-autosize) owns those and
                              // throws ("Using style.minHeight for <TextareaAutosize/>
                              // is not supported. Please use minRows.") — which blanks
                              // the whole post-session page. minRows={2} sets the floor.
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  ) : (
                    <Textarea
                      value={responses[q.key] ?? ''}
                      onChange={e => onChange(q.key, e.currentTarget.value)}
                      placeholder={q.placeholder}
                      autosize
                      minRows={2}
                      maxRows={8}
                      styles={{
                        input: {
                          borderColor: FIELD_BORDER,
                          borderRadius: 8,
                          color: FIELD_TEXT,
                          fontSize: 13,
                          lineHeight: 1.55,
                        },
                      }}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>

          {/* MED-1035 Part B (AC3-AC8): Participant Concerns picker at the bottom
            of the card, after the Carryover question. Medley coaches only. */}
          {canFlagConcerns && (
            <>
              <Divider />
              <ConcernFlagsSection
                participants={participants}
                flags={concernFlags}
                onFlagsChange={onConcernFlagsChange}
                helperText={CONCERN_HELPER_TEXT}
                variant="polished"
              />
            </>
          )}

          <Box style={BLEED_DIVIDER_STYLE} />

          {/* Footer — status indicator (AC6: timestamp dropped) + title-case
            actions (AC7). Submit uses the shared navy post-session primary. */}
          <Group justify="space-between" align="center">
            <Text fz={12} c={HELPER_COLOR}>
              {isSubmitted ? 'Submitted' : 'Draft — not yet submitted'}
            </Text>
            <Group gap="xs">
              <Button
                variant="default"
                size="sm"
                onClick={onSaveDraft}
                loading={saving}
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                onClick={onSubmit}
                loading={saving}
                styles={{
                  root: {
                    backgroundColor: POST_SESSION_PRIMARY_BG,
                    color: '#FFFFFF',
                  },
                }}
              >
                {isSubmitted ? 'Update Feedback' : 'Submit Feedback'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Collapse>
    </ContentCard>
  );
}

export default MedleyFeedbackFormCard;
